import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Modal } from '@react-apps/design-system';
import { useSaleWebhookInboxRow } from '../hooks/use-sale-webhook-inbox';
import { useProviderAccounts } from '../hooks/use-provider-accounts';
import {
  ErrorState,
  Mono,
  WebhookInboxStatusBadge,
  formatDateTime,
  isNotFoundError,
  webhookEventTypeLabel,
} from '../shared/display';
import { JsonBlock } from './JsonPayloadModal';

/**
 * A row do inbox que produziu um evento (`correlationId` = id da row). O
 * payload tem dado de pessoa: aparece na tela, para a mesma audiência que já o
 * lê no Webhook Inbox, e nunca em `console.*` nem na mensagem de erro.
 */
export function WebhookOriginModal({
  inboxId,
  open,
  onClose,
}: {
  inboxId: string;
  open: boolean;
  onClose: () => void;
}) {
  const { data, isLoading, isError, error, refetch } = useSaleWebhookInboxRow(
    inboxId,
    open,
  );
  const { data: accounts } = useProviderAccounts();
  const accountName = accounts?.find(
    (a) => a.id === data?.providerAccountId,
  )?.displayName;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Webhook de origem"
      size="lg"
      footer={
        data ? (
          <Link
            to={`/sales/webhook-inbox?idempotenceSource=${encodeURIComponent(data.idempotenceSource)}`}
            className="text-(--color-accent) text-sm font-medium hover:underline"
          >
            Abrir no Webhook Inbox
          </Link>
        ) : undefined
      }
    >
      {isLoading ? (
        <div
          role="status"
          aria-label="Carregando webhook"
          className="bg-(--color-bg-secondary) h-[60vh] animate-pulse rounded-lg"
        />
      ) : isError ? (
        isNotFoundError(error) ? (
          <p className="text-(--color-text-secondary) py-8 text-center text-sm">
            Webhook não encontrado.
          </p>
        ) : (
          <ErrorState
            message="Não foi possível carregar o webhook."
            onRetry={() => void refetch()}
          />
        )
      ) : data ? (
        <div className="space-y-4">
          <div className="text-(--color-text-secondary) flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
            <WebhookInboxStatusBadge
              status={data.status}
              statusReason={data.statusReason}
              statusDetail={data.statusDetail}
            />
            <span>{webhookEventTypeLabel(data.eventType)}</span>
            <span>{accountName ?? <Mono>{data.providerAccountId}</Mono>}</span>
            <span>Recebido em {formatDateTime(data.createdAt)}</span>
          </div>
          <JsonBlock value={data.payload} />
        </div>
      ) : null}
    </Modal>
  );
}

/** Botão da linha do evento. Fica montado com o modal fechado: o foco volta a ele. */
export function WebhookOriginButton({ inboxId }: { inboxId: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-(--color-text-secondary) hover:text-(--color-accent) focus-visible:ring-(--color-accent) rounded text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2"
      >
        Ver webhook de origem
      </button>
      <WebhookOriginModal
        inboxId={inboxId}
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
